
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function AddExpense() {
  const navigate = useNavigate();
  const [amount, setAmount] = useState('');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const categories = [
    { id: 'food', name: 'طعام', icon: 'ri-restaurant-line', color: 'bg-green-100 text-green-600' },
    { id: 'transport', name: 'مواصلات', icon: 'ri-car-line', color: 'bg-blue-100 text-blue-600' },
    { id: 'utilities', name: 'فواتير', icon: 'ri-bill-line', color: 'bg-yellow-100 text-yellow-600' },
    { id: 'entertainment', name: 'ترفيه', icon: 'ri-gamepad-line', color: 'bg-purple-100 text-purple-600' },
    { id: 'health', name: 'صحة', icon: 'ri-heart-pulse-line', color: 'bg-red-100 text-red-600' },
    { id: 'shopping', name: 'تسوق', icon: 'ri-shopping-bag-line', color: 'bg-pink-100 text-pink-600' },
    { id: 'education', name: 'تعليم', icon: 'ri-book-line', color: 'bg-indigo-100 text-indigo-600' },
    { id: 'other', name: 'أخرى', icon: 'ri-more-line', color: 'bg-gray-100 text-gray-600' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || !title || !category) return;

    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      navigate('/expenses');
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="fixed top-0 w-full bg-white shadow-sm z-50">
        <div className="flex items-center justify-between px-6 py-4">
          <button 
            onClick={() => navigate('/expenses')}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100"
          >
            <i className="ri-arrow-right-line text-xl text-gray-700"></i>
          </button>
          <h1 className="text-xl font-bold text-gray-800">إضافة مصروف</h1>
          <div className="w-10"></div>
        </div>
      </div>

      {/* Content */}
      <div className="pt-20 pb-24 px-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Amount Input */}
          <div className="bg-gradient-to-r from-red-500 to-pink-500 rounded-2xl p-6 text-white">
            <div className="text-center">
              <p className="text-red-100 text-sm mb-2">المبلغ</p>
              <div className="flex items-center justify-center">
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0"
                  className="bg-transparent text-3xl font-bold text-center text-white placeholder-red-200 border-none outline-none w-full"
                  style={{ appearance: 'textfield' }}
                />
                <span className="text-2xl font-bold mr-2">ريال</span>
              </div>
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">عنوان المصروف</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="مثال: فاتورة الكهرباء"
              className="w-full px-4 py-4 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>

          {/* Category Selection */}
          <div>
            <label className="block text-gray-700 font-medium mb-3">الفئة</label>
            <div className="grid grid-cols-2 gap-3">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setCategory(cat.id)}
                  className={`flex items-center space-x-3 space-x-reverse p-4 rounded-2xl border-2 transition-all ${
                    category === cat.id
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${cat.color}`}>
                    <i className={`${cat.icon} text-lg`}></i>
                  </div>
                  <span className="font-medium text-gray-800">{cat.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Date */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">التاريخ</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-4 py-4 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">ملاحظات (اختياري)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="أضف ملاحظات إضافية..."
              rows={3}
              maxLength={500}
              className="w-full px-4 py-4 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
            />
            <p className="text-gray-400 text-sm mt-1">{notes.length}/500</p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!amount || !title || !category || isLoading}
            className="w-full bg-red-600 text-white py-4 rounded-2xl font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-200 hover:scale-105 active:scale-95"
          >
            {isLoading ? (
              <div className="flex items-center justify-center space-x-2 space-x-reverse">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>جاري الحفظ...</span>
              </div>
            ) : (
              'حفظ المصروف'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
